import type { RuleOverrides } from "../../data/ruleRegistry";
import type {
	MaterialPhase,
	MaterialPhaseGroup,
	MaterialRow,
} from "../../engines/types";
import { getT } from "../../i18n";
import type { Dictionary } from "../../i18n/types";
import type { Market } from "../location/types";
import { estimateMaterialRowPrice } from "../pricing/priceEstimator";

const PHASE_ORDER: MaterialPhase[] = [
	"foundation",
	"deck",
	"framing",
	"roofExterior",
	"interior",
	"systems",
];

const PHASE_KEY_MAP: Record<MaterialPhase, keyof Dictionary> = {
	foundation: "domain_phase_foundation",
	deck: "domain_phase_deck",
	framing: "domain_phase_framing",
	roofExterior: "domain_phase_roofExterior",
	interior: "domain_phase_interior",
	systems: "domain_phase_systems",
};

interface GroupMaterialsByPhaseOptions {
	hasRelaxRoom?: boolean;
}

function inferPhase(row: MaterialRow): MaterialPhase {
	if (row.phase) return row.phase;
	const haystack = `${row.material} ${row.dimension}`.toLowerCase();
	if (
		haystack.includes("plint") ||
		haystack.includes("bärlina") ||
		haystack.includes("golv")
	)
		return "foundation";
	if (haystack.includes("trall") || haystack.includes("altan")) return "deck";
	if (
		haystack.includes("regel") ||
		haystack.includes("syll") ||
		haystack.includes("hammarband")
	)
		return "framing";
	if (
		haystack.includes("tak") ||
		haystack.includes("fasad") ||
		haystack.includes("råspont") ||
		haystack.includes("ytter") ||
		haystack.includes("underlagspapp") ||
		haystack.includes("ytpapp") ||
		haystack.includes("takplåt") ||
		haystack.includes("takfotsplåt") ||
		haystack.includes("vindskiveplåt") ||
		haystack.includes("nockbeslag")
	)
		return "roofExterior";
	if (
		haystack.includes("panel") ||
		haystack.includes("isolering") ||
		haystack.includes("lav")
	)
		return "interior";
	return "systems";
}

export function groupMaterialsByPhase(
	rows: MaterialRow[],
	_overrides?: RuleOverrides,
	market: Market = "se",
	options: GroupMaterialsByPhaseOptions = {},
): MaterialPhaseGroup[] {
	const t = getT(market);
	const buckets = new Map<MaterialPhase, MaterialRow[]>();
	for (const row of rows) {
		if (
			options.hasRelaxRoom === false &&
			row.materialKey?.startsWith("partition.")
		) {
			continue;
		}
		const phase = inferPhase(row);
		const list = buckets.get(phase) ?? [];
		list.push({ ...row, phase });
		buckets.set(phase, list);
	}

	return PHASE_ORDER.map((phase) => {
		const phaseRows = buckets.get(phase) ?? [];
		const estimatedCostSek = phaseRows.reduce(
			(sum, row) =>
				sum + estimateMaterialRowPrice(row, { mode: "baseline" }).price,
			0,
		);
		return {
			phase,
			title: t[PHASE_KEY_MAP[phase]] as string,
			rows: phaseRows,
			estimatedCostSek,
		};
	}).filter((group) => group.rows.length > 0);
}
